import { useState } from 'react'
import type { PublicSizeResponse } from '../lib/api'
import { formatPct } from '../lib/format'
import { cn } from '../lib/cn'
import { COMPARISON_LABELS } from '../lib/labels'

interface Props {
  methodology: PublicSizeResponse['methodology']
  isFallback: boolean
}

export function MethodologyPanel({ methodology, isFallback }: Props) {
  const [open, setOpen] = useState(false)
  const m = methodology

  return (
    <div className="mt-4 border border-mist rounded-card bg-white overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-chalk transition-colors"
      >
        <span className="text-sm font-medium text-navy">How we get to this number</span>
        <span className={cn('text-xs font-data text-mint transition-transform', open && 'rotate-180')}>
          ▼
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 text-sm text-navy/80 space-y-4 border-t border-mist">
          {isFallback && (
            <div className="text-xs italic text-navy/60 bg-foam px-3 py-2 rounded">
              Showing reference methodology — live values resume when the sizing service responds.
            </div>
          )}

          <MethodRow label="Discount rate (OMB Circular A-94)" value={`${formatPct(m.discountRatePct)} beginning-of-year`} />
          <MethodRow label="Refresh reserve (GAO-aligned)" value={`${formatPct(m.refreshReserveAnnualPct)}/yr accrual, starting Year 2`} />
          <MethodRow
            label="Per-category escalation (Year 2+)"
            value={`Compute ${formatPct(m.perCategoryEscalation.compute)} · Power ${formatPct(m.perCategoryEscalation.power)} · Labor ${formatPct(m.perCategoryEscalation.labor)} · Colo ${formatPct(m.perCategoryEscalation.colo)}`}
          />
          <MethodRow
            label="STIG / ATO sustainment"
            value={m.stigComplianceAnnual === 'included' ? 'Included in the comparison line' : 'Excluded — customer adds separately'}
          />

          <div className="bg-chalk border border-mist rounded px-4 py-3">
            <div className="text-xs font-data uppercase tracking-wide text-navy/50 mb-1">
              {COMPARISON_LABELS.diyFederal}
            </div>
            <div className="text-sm text-navy/80 leading-relaxed">
              Customer attempts FedRAMP-aligned multi-tenant private cloud in their own
              data center using their hypervisor choice (VCF, OpenStack, or KVM-based).
              Includes hardware, licensing, FTE, refresh reserve, and ATO sustainment.
              Numbers reflect a single tenant carrying the full platform load — without
              consolidating commercial-stack economics across customers the way an operator
              does.
            </div>
          </div>

          <div>
            <MethodRow
              label={`FTE allocation (${COMPARISON_LABELS.diyFederal})`}
              value={m.fteAllocationBand}
            />
            <div className="mt-2 text-xs text-navy/60 italic bg-foam/50 px-3 py-2 rounded leading-relaxed">
              FTE allocation reflects the {COMPARISON_LABELS.diyFederal} column — a customer
              attempting to deliver their own sovereign cloud at this workload scale.
              CyCloud subscribers do not carry this FTE. CyLogic operates the platform on
              subscribers' behalf and amortizes the team across the customer base.
            </div>
          </div>

          <div>
            <div className="text-xs font-data uppercase text-navy/50 tracking-wide mb-2">
              Hyperscaler comparison — what's actually summed
            </div>
            <div className="bg-foam/50 border border-mist rounded px-3 py-2 mb-3">
              <div className="text-xs text-navy/80 leading-relaxed">
                Each hyperscaler column sums per-capability per-vCPU / per-GB / per-cluster
                rows from <span className="font-data">cloud_capability_costs</span>, not a
                single instance family. Twelve basket categories:
              </div>
              <div className="mt-2 text-xs font-data text-navy/70 leading-relaxed">
                {basketLabels(m.hyperscalerComparison.comparisonBasket).join(' · ')}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-chalk px-3 py-2 rounded">
                <div className="text-xs font-medium text-navy">{COMPARISON_LABELS.awsGovCloud}</div>
                <div className="text-[10px] font-data uppercase tracking-wide text-navy/50 mt-1">
                  Representative procurement family
                </div>
                <div className="text-xs font-data text-navy/70">
                  {m.hyperscalerComparison.awsGovCloud.representativeFamily}
                  <br />
                  {m.hyperscalerComparison.awsGovCloud.region}
                  <br />
                  {m.hyperscalerComparison.awsGovCloud.commitmentPosture}
                </div>
              </div>
              <div className="bg-chalk px-3 py-2 rounded">
                <div className="text-xs font-medium text-navy">{COMPARISON_LABELS.azureGov}</div>
                <div className="text-[10px] font-data uppercase tracking-wide text-navy/50 mt-1">
                  Representative procurement family
                </div>
                <div className="text-xs font-data text-navy/70">
                  {m.hyperscalerComparison.azureGov.representativeFamily}
                  <br />
                  {m.hyperscalerComparison.azureGov.region}
                  <br />
                  {m.hyperscalerComparison.azureGov.commitmentPosture}
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-navy/50 font-data pt-2 border-t border-mist">
            Methodology baseline: {m.comparisonConstructedDate}
          </div>
        </div>
      )}
    </div>
  )
}

function MethodRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div className="text-xs font-data uppercase text-navy/50 tracking-wide">{label}</div>
      <div className="sm:col-span-2 text-sm">{value}</div>
    </div>
  )
}

const BASKET_LABEL: Record<string, string> = {
  compute: 'compute',
  memory: 'memory',
  storage_iops: 'storage IOPS',
  egress_nat: 'egress / NAT',
  load_balancing: 'load balancing',
  kubernetes_platform: 'Kubernetes platform',
  monitoring_logging: 'monitoring / logging',
  automation_iac: 'automation / IaC',
  ai_ml_platform: 'AI/ML platform',
  migration_tooling: 'migration tooling',
  lifecycle_management: 'lifecycle management',
  operations_fte: 'operations FTE',
}

function basketLabels(keys: string[]): string[] {
  return keys.map((k) => BASKET_LABEL[k] ?? k)
}
